//React
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Slide, toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NativeSupportHandlerV2 } from '@bharatpe/native-support-handler';

import { setGeneralDetails as setGeneralDetailsAction } from 'redux/actions/Local/setGeneralDetails';
import { triggerFetchKycStatusApiCall as triggerFetchKycStatusApiCallAction } from 'redux/actions/Api/General/fetchKycStatus';
import { triggerFetchCardDetailsApiCall as triggerFetchCardDetailsApiCallAction } from 'redux/actions/Api/CardFlow/fetchCardDetails';
import { triggerFetchTxnListApiCall as triggerFetchTxnListApiCallAction } from 'redux/actions/Api/TransactionFlow/fetchTxnList';
import { triggerHomeDetailsApiCall as triggerHomeDetailsApiCallAction } from 'redux/actions/Api/General/homeDetails';
import { triggerRepaymentStatusApiCall as triggerRepaymentStatusApiCallAction } from 'redux/actions/Api/TransactionFlow/repaymentStatus';
import { triggerValidateVpaApiCall as triggerValidateVpaApiCallAction } from 'redux/actions/Api/PostPaidFlow/validateVpa';
import { triggerFetchRepaymentHistoryApiCall as triggerFetchRepaymentHistoryApiCallAction } from 'redux/actions/Api/PostPaidFlow/fetchRepaymentHistory';
import { orderDetails } from 'containers/CreditCardFlow/Common';
import {
	triggerTxnStatusApiCallSuccess as triggerTxnStatusApiCallSuccessAction,
	triggerTxnStatusApiCall as triggerTxnStatusApiCallAction,
} from 'redux/actions/Api/TransactionFlow/txnStatus';

import ChangeHistory from 'utils/ChangeHistory';
import { getURLParameter } from 'utils/getURLParameter';
import history from 'utils/history';
import { getAppVersion, isLatestApp, openAppUpdateUrl } from 'utils/appUpdateHelper';
import nativeBack from 'utils/nativeBackSubscription';
import nativeHandler from 'utils/nativeHandler';

import { useFeatureAccess } from 'Hooks/useFeatureAccess';
import globalConstant from 'constants/globalConstant';
import AppRouter from './routes';
import APP_ROUTES_CONSTANT from 'constants/appRouteConstants';
import GLOBAL_CONSTANT from 'constants/globalConstant';
import { homeDetailsUtil } from 'utils/homeDetailUtil';
import { fetchServiceDetails } from 'utils/thirdPartyUtils';
import { isSuperAppHome, PostpeV1UserApi } from 'utils/isSuperAppHome';

const AccountBlockedModal = lazy(() => import('components/molecules/Modal/AccountBlockedModal'));
const UpdateModal = lazy(() => import('components/molecules/Modal/UpdateModal'));
const ScreenLockModal = lazy(() => import('components/molecules/Modal/ScreenLockModal'));

const App = () => {
	const dispatch = useDispatch();

	const [updateModalVisibility, setUpdateModalVisibility] = useState(false);
	const [screenLockModalVisibility, setScreenLockModalVisibility] = useState(false);

	const generalDetails = useSelector((state) => state?.generalDetails);
	const featureAccess = useSelector((state) => state?.featureAccess);
	const homeDetails = useSelector((state) => state?.homeDetails);
	const cardDetails = useSelector((state) => state?.cardDetails);

	useFeatureAccess();
	const setGeneralDetails = (data, callback, stopLoader) =>
		dispatch(setGeneralDetailsAction(data, callback, stopLoader));
	const triggerFetchKycStatusApiCall = (data, callback, stopLoader) =>
		dispatch(triggerFetchKycStatusApiCallAction(data, callback, stopLoader));
	const triggerFetchCardDetailsApiCall = (data, callback, stopLoader) =>
		dispatch(triggerFetchCardDetailsApiCallAction(data, callback, stopLoader));
	const triggerFetchTxnListApiCall = (data, callback, stopLoader) =>
		dispatch(triggerFetchTxnListApiCallAction(data, callback, stopLoader));
	const triggerHomeDetailsApiCall = (data, callback, stopLoader) =>
		dispatch(triggerHomeDetailsApiCallAction(data, callback, stopLoader));
	const triggerRepaymentStatusApiCall = (data, callback, stopLoader) =>
		dispatch(triggerRepaymentStatusApiCallAction(data, callback, stopLoader));
	const triggerValidateVpaApiCall = (data, callback, stopLoader) =>
		dispatch(triggerValidateVpaApiCallAction(data, callback, stopLoader));
	const triggerFetchRepaymentHistoryApiCall = (data, callback, stopLoader) =>
		dispatch(triggerFetchRepaymentHistoryApiCallAction(data, callback, stopLoader));
	const triggerTxnStatusApiCallSuccess = (data, callback, stopLoader) =>
		dispatch(triggerTxnStatusApiCallSuccessAction(data, callback, stopLoader));
	const triggerTxnStatusApiCall = (data, callback, stopLoader) =>
		dispatch(triggerTxnStatusApiCallAction(data, callback, stopLoader));

	//Actions
	const reloadPage = () => {
		history.push('/');
		setTimeout(() => {
			history.push(APP_ROUTES_CONSTANT.postpaid.home);
		}, 0);
	};

	const handleRepaymentHistoryResponse = (data) => {
		if (!data?.repaymentHistory?.length) return ChangeHistory('push', APP_ROUTES_CONSTANT.postpaid.home);
		const item = data?.repaymentHistory[0];
		if (['SUCCESS', 'FAILURE', 'CANCELLED'].includes(item?.paymentStatus)) {
			triggerTxnStatusApiCallSuccess({
				...item,
				path: 'home',
			});
			ChangeHistory('push', `/transaction-status`);
			return;
		}
		nativeHandler.openDeepLinkWithCallback(
			`postpe://dynamic?key=bp-pg&txnId=${item.pgTxnId}&clientName=UE9TVFBF&entity=CONSUMER`,
			() => {
				nativeHandler.removeLoader();
			},
		);
	};

	const handleCrmRepaymentWroute = () =>
		triggerFetchRepaymentHistoryApiCall(
			{ pageNum: 0, pageSize: 1, dontAppend: true, source: 'CRM' },
			handleRepaymentHistoryResponse,
			true,
		);

	const actionOnBasisOnWroute = (homeData) => {
		const wrouteValue = getURLParameter(globalConstant.URL_PARAMETER.WROUTE) || '';

		if (!['ACTIVE'].includes(homeData?.accountStatus) && !isSuperAppHome()) homeDetailsUtil(homeData, featureAccess);

		if (
			homeData?.accountStatus === 'ACTIVE' &&
			homeData?.postPaidAccount?.lender === GLOBAL_CONSTANT.LENDER.TRILLION &&
			!['ccTxnStatus', 'creditCard', 'txnStatus'].includes(wrouteValue)
		)
			return ChangeHistory('push', '/lender-migration');
		if (
			homeData?.accountStatus === 'ACTIVE' &&
			homeData?.postPaidAccount?.lender === GLOBAL_CONSTANT.LENDER.LDC &&
			!['ccTxnStatus', 'creditCard', 'txnStatus'].includes(wrouteValue)
		)
			return ChangeHistory('push', APP_ROUTES_CONSTANT.onboarding.upgrade);

		if (homeData?.verifyPan) return ChangeHistory('push', '/onboarding/verify-pan');
		if (
			['NA', 'INELIGIBLE', 'WAITLIST', 'INIT'].includes(homeData?.accountStatus) &&
			!featureAccess.onboardingEnabled &&
			!isSuperAppHome()
		)
			return ChangeHistory('push', APP_ROUTES_CONSTANT.onboarding.upgrade);

		switch (wrouteValue) {
			case globalConstant.WROUTE.ONBOARDING_UPGRADE:
				ChangeHistory('push', '/onboarding/upgrade-screen'); //redo
				break;
			case globalConstant.WROUTE.CCBP_EDUCATION:
				ChangeHistory('push', '/stayTuned');
				// ChangeHistory('push', '/emergency-fund-education');
				break;

			case globalConstant.WROUTE.auCc:
				ChangeHistory('push', '/au-credit-card');
				break;

			case globalConstant.WROUTE.FLEXCARDS:
				ChangeHistory('push', '/tnc', { activeItem: 'FLEXCARDS' });
				break;

			case globalConstant.WROUTE.SOFT_UPDATE_APP:
				reloadPage();
				setTimeout(() => {
					openAppUpdateUrl();
				}, 500);

				break;

			case 'customerRef':
				ChangeHistory('push', '/onboarding/add-reference');
				break;

			case globalConstant.WROUTE.SEND_MONEY:
				if (homeData?.accountStatus === 'ACTIVE' && homeData?.kycLevel === 2) {
					ChangeHistory('push', '/send-money/home');
				} else {
					homeDetailsUtil(homeData, featureAccess);
				}
				break;

			case globalConstant.WROUTE.CHARGES:
				ChangeHistory('push', '/charges');
				break;

			case globalConstant.WROUTE.OFFERS_TEST:
			case globalConstant.WROUTE.OFFERS:
				ChangeHistory('push', '/postpaid/offers');
				break;

			case globalConstant.WROUTE.TNC:
				ChangeHistory('push', '/tnc');
				nativeHandler.removeLoader();
				break;

			case globalConstant.WROUTE.AGREEMENT_ONBOARDING:
				ChangeHistory('push', '/agreement', { type: 'ONBOARDING_BUREAU_CONSENT', lenderType: 'LL' });
				break;

			case globalConstant.WROUTE.REFER_N_EARN:
				ChangeHistory('push', '/refernearn');
				break;

			case globalConstant.WROUTE.CONSUMER_TXN_STATUS:
				nativeHandler.showLoader({
					first_message: 'fetching transaction',
					start_index: 0,
					range: 8,
					second_message: 'details',
				});
				triggerTxnStatusApiCall({
					txnId: getURLParameter(globalConstant.URL_PARAMETER.ID),
					path: 'home',
				});

				break;

			case globalConstant.WROUTE.TXN_LIST:
				triggerFetchTxnListApiCall(
					{
						pageNum: 0,
						pageSize: 100,
					},
					() => {
						ChangeHistory('push', '/transaction-list');
					},
					true,
				);
				break;
			case globalConstant.WROUTE.PROFILE:
				ChangeHistory('push', '/profile');
				break;

			case globalConstant.WROUTE.CC_TXN_LIST:
				ChangeHistory('push', '/stayTuned');
				break;

			case globalConstant.WROUTE.SCRATCHCARD:
				ChangeHistory('push', '/cashback-rewards');
				break;

			case globalConstant.WROUTE.ACTIVATE_CARD:
				reloadPage();
				nativeHandler
					.activateCard()
					.then(() => {
						reloadPage();
						toast.success('Pin set successfully');
					})
					.catch((errorMessage) => {
						setScreenLockModalVisibility(errorMessage === 'openSettings');
						// reloadPage();
					});
				break;
			case globalConstant.WROUTE.BILL:
				ChangeHistory('push', `/postpaid/${homeData?.postPaidAccount?.billAmount ? 'bill-view' : 'home'}`);
				break;

			case globalConstant.WROUTE.SCAN_N_PAY:
				if (homeData?.accountStatus === 'ACTIVE' && homeData?.kycLevel === 2) {
					nativeHandler
						.openScanner()
						.then((vpa) => {
							if (vpa) {
								triggerValidateVpaApiCall({
									vpa,
								});
							} else {
								ChangeHistory('push', APP_ROUTES_CONSTANT.postpaid.home);
							}
						})
						.catch((errorMessage) => {
							setScreenLockModalVisibility(errorMessage === 'openSettings');
							ChangeHistory('push', APP_ROUTES_CONSTANT.postpaid.home);
						});
				} else {
					homeDetailsUtil(homeData, featureAccess);
				}
				break;

			case globalConstant.WROUTE.CARD:
				triggerFetchCardDetailsApiCall(
					null,
					() => {
						ChangeHistory('push', APP_ROUTES_CONSTANT.postpaid.home);
						nativeHandler.openDeepLinkWithCallback('postpe://zeta', (webRoute) => {
							switch (webRoute) {
								case 'OrderCard':
									localStorage.setItem('openZeta', 'true');
									ChangeHistory(
										'push',
										cardDetails?.applicationStatus?.toLowerCase() === 'draft'
											? '/order-card/landing'
											: '/order-card/delivery-status',
									);
									break;

								case 'IncreaseLimit':
									triggerFetchKycStatusApiCall(null, () => {
										triggerFetchKycStatusApiCall(
											{
												dontOpenDeeplink: true,
												hasCallback: true,
											},
											() => {
												reloadPage();
											},
										);
									});
									break;

								case 'ActivityClosed':
									reloadPage();
									break;

								default:
									break;
							}
						});
					},
					true,
				);
				break;
			case globalConstant.WROUTE.TXN_STATUS:
				nativeHandler.showLoader({
					first_message: 'fetching transaction',
					start_index: 0,
					range: 8,
					second_message: 'details',
				});
				triggerRepaymentStatusApiCall({
					orderId: getURLParameter(globalConstant.URL_PARAMETER.ORDER_ID),
					path: 'home',
				});
				break;
			case globalConstant.WROUTE.CC_TXN_STATUS:
				ChangeHistory('push', '/stayTuned');

				// nativeHandler.showLoader({
				// 	first_message: 'fetching transaction',
				// 	start_index: 0,
				// 	range: 8,
				// 	second_message: 'details',
				// });
				// orderDetails(getURLParameter(globalConstant.URL_PARAMETER.ORDER_ID));
				break;
			case globalConstant.WROUTE.CREDIT_CARD:
				ChangeHistory('push', '/stayTuned');
				// nativeHandler.showLoader({
				// 	first_message: 'loading',
				// 	start_index: 0,
				// 	range: 7,
				// 	second_message: 'please wait',
				// });
				// const creditCardWroute = async () => {
				// 	featureAccess?.firstBillPayment
				// 		? ChangeHistory('push', '/credit-card-tnc', featureAccess?.firstBillPayment)
				// 		: ChangeHistory('push', '/credit-card-list', featureAccess?.firstBillPayment);
				// };
				// creditCardWroute();
				// nativeHandler.removeLoader();
				break;
			// nativeHandler.showLoader({
			// 	first_message: 'loading',
			// 	start_index: 0,
			// 	range: 7,
			// 	second_message: 'please wait',
			// });
			// const creditCardWroute = async () => {
			// 	featureAccess?.firstBillPayment
			// 		? ChangeHistory('push', '/credit-card-tnc', featureAccess?.firstBillPayment)
			// 		: ChangeHistory('push', '/credit-card-list', featureAccess?.firstBillPayment);
			// };
			// creditCardWroute();
			// nativeHandler.removeLoader();
			// break;
			case globalConstant.WROUTE.CC_SECURE_NOW:
				ChangeHistory('push', '/stayTuned');
				// nativeHandler.showLoader({
				// 	first_message: 'loading',
				// 	start_index: 0,
				// 	range: 7,
				// 	second_message: 'please wait',
				// });
				// const secureNowWroute = async () => {
				// 	ChangeHistory('push', '/credit-card/secure-card', { secureCardFromTxn: false });
				// };
				// secureNowWroute();
				// nativeHandler.removeLoader();
				break;
			// nativeHandler.showLoader({
			// 	first_message: 'loading',
			// 	start_index: 0,
			// 	range: 7,
			// 	second_message: 'please wait',
			// });
			// const secureNowWroute = async () => {
			// 	ChangeHistory('push', '/credit-card/secure-card', { secureCardFromTxn: false });
			// };
			// secureNowWroute();
			// nativeHandler.removeLoader();
			// break;
			case globalConstant.WROUTE.CRM_PAYMENT_LINK:
				handleCrmRepaymentWroute();
				break;
			case globalConstant.WROUTE.SANCTION_LETTER:
				ChangeHistory('push', '/onboarding/loan-sanction', { isReadBeforeUpdate: false });
				break;
			case globalConstant.WROUTE.QR_CHARGES_SCREEN:
				ChangeHistory('push', '/qr-charges');
				break;
			case globalConstant.WROUTE.CLEAR_DUES:
				ChangeHistory('push', APP_ROUTES_CONSTANT.postpaid.home);
				break;
			case globalConstant.WROUTE.FAQS:
				ChangeHistory('push', '/faq');
				break;
			case globalConstant.WROUTE.REWARDS_FAQS:
				setGeneralDetails({
					parentFaq: 'Rewards',
				});
				ChangeHistory('push', APP_ROUTES_CONSTANT.faqs.detailed);
				break;
			case globalConstant.WROUTE.WAITLIST:
				ChangeHistory('push', '/onboarding/waitlist');
				break;
			case globalConstant.WROUTE.RATING:
				ChangeHistory('push', '/rating');

				break;
			case globalConstant.WROUTE.CASH_BACK:
				ChangeHistory('push', '/postpaid/cashback');
				break;
			case globalConstant.WROUTE.CARD_CLOSURE:
				ChangeHistory('push', '/postpaid/card-closed');
				break;
			case globalConstant.WROUTE.BBPS_CHILL_FEST_KNOW_MORE:
				ChangeHistory('push', APP_ROUTES_CONSTANT.postpaid.home);
				break;
			case globalConstant.WROUTE.CHECK_LOAN_ELIGIBILITY:
				ChangeHistory('push', '/onboarding/check-loan-eligibility');
				break;
			case globalConstant.WROUTE.LENDER_MIGRATION:
				ChangeHistory('push', '/onboarding/upgrade-screen'); //redo
				break;
			case globalConstant.WROUTE.QR_AMOUNT_INSERTION:
				ChangeHistory('push', '/postpaid/amount-insertion');
				break;
			case globalConstant.WROUTE.CDL_INTRO:
				ChangeHistory('push', '/postpaid/cdl-intro');
				break;
			case globalConstant.WROUTE.BILL_VIEW:
				ChangeHistory('push', '/postpaid/bill-view');
				break;
			case globalConstant.WROUTE.AU_CREDIT_CARD:
				ChangeHistory('push', '/au-credit-card');
				break;

			case globalConstant.WROUTE.BANNER_KNOW_MORE:
				ChangeHistory('push', '/know-more/banner');
				break;
			case globalConstant.WROUTE.LIMITS:
				ChangeHistory('push', '/postpaid/limit-management');
				break;
			case globalConstant.WROUTE.LOANS:
			case globalConstant.WROUTE.POSTPAID_LOANS:
				ChangeHistory('push', '/postpaid/loans');
				break;
			case globalConstant.WROUTE.EMI_CONVERSION:
				ChangeHistory('push', '/postpaid/emi-conversion', {
					activeTenure: getURLParameter(GLOBAL_CONSTANT.URL_PARAMETER.ACTIVE_TENURE),
				});
				break;
			case globalConstant.WROUTE.ACCOUNT_DETAILS:
				ChangeHistory('push', '/postpaid/account-details');
				break;
			case globalConstant.WROUTE.POSTPE_REPAYMENT:
				ChangeHistory('push', '/postpaid/repayment-history');
				break;
			case globalConstant.WROUTE.POSTPE_BILL_HISTORY:
				ChangeHistory('push', '/postpaid/bill-history');
				break;
			case globalConstant.WROUTE.POSTPE_VIDEOS:
				ChangeHistory('push', '/postpe-videos');
				break;
			case globalConstant.WROUTE.POSTPE_PRIVACY_SETTING:
				ChangeHistory('push', '/privacy-settings');
				break;
			default:
				break;
		}
	};

	const initialApis = async () => {
		triggerHomeDetailsApiCall(
			{
				firstTimeCall: true,
				stopRoute: getURLParameter(globalConstant.URL_PARAMETER.WROUTE)?.length > 0,
			},
			(data) => {
				if (
					getURLParameter(globalConstant.URL_PARAMETER.WROUTE) &&
					[
						'txnList',
						'ccTxnList',
						'postpePrivacySetting',
						'postpeVideos',
						'postpeBillHistory',
						'postpeRepayment',
						'ccTxnStatus',
						'creditCard',
						'txnStatus',
						'consumerTxnStatus',
						'auCreditCard',
						'dsaLoanEligibility',
						'checkLoanEligibility',
						'agreementOnboarding',
						'tnc',
						'onboardingUpgrade',
						'waitlist',
						'lenderMigration',
						'cashBack',
						'scratchCards',
						'charges',
						'faqs',
						'bannerKnowMore',
						'profile',
						'billView',
						'limits',
						'rating',
						'ccbpEducation',
						'emiConversion',
						'postpaidLoans',
						'tnc',
						'offers',
						'ccTxnList',
						'ccSecureNow',
						'accountDetails',
						'refernearn',
					].includes(getURLParameter(globalConstant.URL_PARAMETER.WROUTE))
				) {
					actionOnBasisOnWroute(data);
				} else if (getURLParameter(globalConstant.URL_PARAMETER.WROUTE)) {
					homeDetailsUtil(data, featureAccess);
				}
			},
			true,
		);
	};

	const commonCheck = () => {
		setTimeout(() => {
			isLatestApp()
				? setUpdateModalVisibility(true)
				: getURLParameter(globalConstant.URL_PARAMETER.WROUTE) === globalConstant.WROUTE.TNC
				? ChangeHistory('push', '/tnc')
				: initialApis();
		}, 0);
	};

	useEffect(() => {
		if (process.env.NODE_ENV === 'development') {
			setGeneralDetails({
				appInfo: {
					appversion: getURLParameter(globalConstant.URL_PARAMETER.APPVERSION),
					cid: getURLParameter(globalConstant.URL_PARAMETER.CID),
					consumerId: getURLParameter(globalConstant.URL_PARAMETER.CONSUMER_ID),
					deviceId: getURLParameter(globalConstant.URL_PARAMETER.DEVICE_ID),
					installId: getURLParameter(globalConstant.URL_PARAMETER.INSTALL_ID),
					is_virtual: getURLParameter(globalConstant.URL_PARAMETER.IS_VIRTUAL),
					latitude: getURLParameter(globalConstant.URL_PARAMETER.LATITUDE),
					longitude: getURLParameter(globalConstant.URL_PARAMETER.LONGITUDE),
					manufacturer: getURLParameter(globalConstant.URL_PARAMETER.MANUFACTURER),
					model: getURLParameter(globalConstant.URL_PARAMETER.MODEL),
					platform: getURLParameter(globalConstant.URL_PARAMETER.PLATFORM),
					routing_key: getURLParameter(globalConstant.URL_PARAMETER.ROUTING_KEY),
					serial: getURLParameter(globalConstant.URL_PARAMETER.SERIAL),
					simId: getURLParameter(globalConstant.URL_PARAMETER.SIM_ID),
					token: getURLParameter(globalConstant.URL_PARAMETER.TOKEN),
					version: getURLParameter(globalConstant.URL_PARAMETER.VERSION),
					visa: getURLParameter(globalConstant.URL_PARAMETER.VISA),
				},
			});
		} else {
			NativeSupportHandlerV2.init().then(() => {
				nativeHandler.getAppInfo().then((appInfo) => {
					setGeneralDetails({
						appInfo,
					});
					nativeBack.subScribeBackButton();
					if (!localStorage.getItem('appInfo')) {
						PostpeV1UserApi().then((response) => {
							setGeneralDetails({ isSuperApp: response });
							if (response && !GLOBAL_CONSTANT.EXCLUDE_WROUTE_SUPERAPP.includes(getURLParameter('wroute')))
								return nativeHandler.openDeepLinkWithCallback(GLOBAL_CONSTANT.POSTPE_V2_DEEPLINK, () =>
									nativeHandler.closeWebView(),
								);
						});
					} else if (isSuperAppHome() && !GLOBAL_CONSTANT.EXCLUDE_WROUTE_SUPERAPP.includes(getURLParameter('wroute')))
						return nativeHandler.openDeepLinkWithCallback(GLOBAL_CONSTANT.POSTPE_V2_DEEPLINK, () =>
							nativeHandler.closeWebView(),
						);
				});
			});
		}

		if (getURLParameter(GLOBAL_CONSTANT.THIRD_PARTY.CLIENT) || getURLParameter(GLOBAL_CONSTANT.THIRD_PARTY.CAT)) {
			nativeHandler.showLoader({
				first_message: 'fetching',
				start_index: 0,
				range: 7,
				second_message: 'best offer for you',
			});
			const urlParameter = getURLParameter(GLOBAL_CONSTANT.THIRD_PARTY.CLIENT);
			if (urlParameter) {
				fetchServiceDetails(urlParameter);
				return;
			}
		}
		nativeHandler.removeLoader();
		if (isSuperAppHome() && !GLOBAL_CONSTANT.EXCLUDE_WROUTE_SUPERAPP.includes(getURLParameter('wroute')))
			return nativeHandler.openDeepLinkWithCallback(GLOBAL_CONSTANT.POSTPE_V2_DEEPLINK, () =>
				nativeHandler.closeWebView(),
			);

		commonCheck();
	}, []);

	useEffect(() => {
		if (isLatestApp()) {
			// if (homeDetails?.postPaidAccount?.billAmount > 0 && !canShowSoftUpdate()) return;
			setUpdateModalVisibility(true);
		}
	}, [homeDetails]);

	useEffect(() => {
		if (!featureAccess.onboardingEnabled) {
			if (homeDetails?.accountStatus === 'CLOSED') return ChangeHistory('push', '/closed');
			else if (homeDetails?.accountStatus === 'INACTIVE')
				return ChangeHistory('push', '/privacy-settings', { flowType: 'REACTIVATE' });
		}
	}, [homeDetails, featureAccess]);

	if (getURLParameter(GLOBAL_CONSTANT.THIRD_PARTY.CLIENT) || getURLParameter(GLOBAL_CONSTANT.THIRD_PARTY.CAT)) {
		return <></>;
	}

	//Return UI
	return (
		<div>
			<div
				className="app"
				unselectable="on"
				onSelectCapture={() => {
					return false;
				}}>
				{!updateModalVisibility && (
					<div className="router">
						<AppRouter />
					</div>
				)}
			</div>
			{generalDetails?.blockingModal && (
				<Suspense fallback={<></>}>
					{' '}
					<AccountBlockedModal {...generalDetails?.blockingModal} />
				</Suspense>
			)}
			<ToastContainer
				position={toast.POSITION.BOTTOM_CENTER}
				autoClose={2500}
				hideProgressBar={true}
				closeButton={false}
				transition={Slide}
				draggable={false}
				pauseOnFocusLoss={false}
				closeOnClick={false}
				className="toaster"
				limit={1}
			/>
			{updateModalVisibility && (
				<Suspense fallback={<></>}>
					{' '}
					<UpdateModal onClose={() => setUpdateModalVisibility(false)} />
				</Suspense>
			)}

			{screenLockModalVisibility && (
				<Suspense fallback={<></>}>
					{' '}
					<ScreenLockModal onClose={() => setScreenLockModalVisibility(false)} />
				</Suspense>
			)}
		</div>
	);
};

export default App;
